import axios, { AxiosDefaults, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { dictionary } from '../../idscan/configuration/CaseDictionary';
import { JourneyEvent } from '../../idscan/enums/JourneyEvent';
import { ResponseType } from '../../idscan/enums/ResponseType';
import { TranslationKey } from '../../idscan/enums/Translation';
import { IAuthSettings } from '../../idscan/interfaces/AuthSettings';
import { IEndpoints } from '../../idscan/interfaces/Endpoints';
import { IHttpServiceConfiguration } from '../interfaces/HttpServiceConfiguration';
import { CaseDictionaryService } from './CaseDictionaryService';
import EventService from './EventService';
import { ExceptionManager } from './ExceptionManager';
import { HttpResponse } from './HttpResponse';
import StorageService from './StorageService';
import { TransformationService } from './TransformationService';
import { STORAGE_KEYS } from '../../idscan/configuration/StorageKeys';

/**
 * @internal
 */
export class HttpService {

	public get auth(): boolean {
		return !!this._axios.defaults.headers.common['Authorization'];
	}

	public get authSettings(): boolean | IAuthSettings {
		return this._httpConfig!.authSettings;
	}

	public get endpoints(): IEndpoints {
		return this._httpConfig!.endpoints;
	}

	private get tokenName() {
		return this._httpConfig!.authSettings && this._httpConfig!.authSettings.persistence.tokenName ? this._httpConfig!.authSettings.persistence.tokenName : undefined;
	}

	private get tokenTTL() {
		return this._httpConfig!.authSettings && this._httpConfig!.authSettings.persistence.expire ? this._httpConfig!.authSettings.persistence.expire : undefined;
	}

	// An instance of the axios library.
	private _axios!: AxiosInstance;

	// An instance of CaseDictionaryService.
	private _caseDictionaryService!: CaseDictionaryService;

	private _httpConfig?: IHttpServiceConfiguration;

	public configure = (httpConfig: IHttpServiceConfiguration): Promise<this> => new Promise((resolve) => {
		this._httpConfig = httpConfig;

		return resolve(this);
	})

	/**
	 * Initializes the internal services
	 */
	public initialize = (): Promise<this> => new Promise((resolve) => {
		this._caseDictionaryService = new CaseDictionaryService(dictionary);
		this.setDefaults();

		return resolve(this);
	})

	/**
	 * Generic post function that takes a url and a data object
	 * @param url The URL for the request
	 * @param data Body Data to send with the request
	 * @param config Additional configuration for the request
	 * @param name The name to assign to the request
	 */
	public post = (url: string, data?: any, config?: AxiosRequestConfig, name: string = ''): Promise<any> => {
		EventService.publish(JourneyEvent.TRANSFER_STARTED, {
			name,
			status: 'started',
			percentage: 0
		});

		return this._axios.post(url, data, {
			onDownloadProgress: this.onDownloadProgress(name),
			onUploadProgress: this.onUploadProgress(name),
			...config
		}).then(this.onPostSuccess(name)).catch(this.onPostFailure(name));
	}
	
	private onPostSuccess = (name: string) => (res: any) => {
		var journeyId = StorageService.get(STORAGE_KEYS.journeyId);
			if (res.journeyId == journeyId || res?.Workflow?.PersonEntryId == journeyId){
			return EventService.publish(JourneyEvent.TRANSFER_COMPLETE, {
				name,
				status: 'complete',
				percentage: 100,
				JourneyId: StorageService.get(STORAGE_KEYS.journeyId)
			}).then(() => res);}
		else {
			return name ? undefined : res;
		}
	}

	private onPostFailure = (name: string) => (e: any) => {
		EventService.publish(JourneyEvent.TRANSFER_FAILED, {
			name,
			status: 'failed',
		});
		throw e;
	}

	/**
	 * Generic get function that takes a url
	 * @param url The URL for the request
	 * @param config Additional configuration for the request
	 * @param name The name to assign to the request
	 */
	public get = (url: string, config?: AxiosRequestConfig, name: string = ''): Promise<any> => {
		EventService.publish(JourneyEvent.TRANSFER_STARTED, {
			name,
			status: 'started',
			percentage: 0,
		});

		return this._axios.get(url, {
			onDownloadProgress: this.onDownloadProgress(name),
			onUploadProgress: this.onUploadProgress(name),
			...config
		}).then((res) => EventService.publish(JourneyEvent.TRANSFER_COMPLETE, {
			name,
			status: 'complete',
			percentage: 100,
		}).then(() => res)
		).catch((e) => {
			EventService.publish(JourneyEvent.TRANSFER_FAILED, {
				name,
				status: 'failed',
			});

			throw e;
		});
	}

	/**
	 * Created for dynamic token injection
	 */
	public injectToken = (token: string): void => {
		this.removeToken();
		this.writeToken(token);
		this.setToken(token);
	}

	/**
	 * Writes the http service defaults to console for debuggins.
	 */
	public debug = (): AxiosDefaults<any> => {
		return this._axios.defaults;
	}

	/**
	 * Tries to get the token from the storage.
	 */
	public getToken = (): string | undefined => {
		try {
			if (this.tokenName) {
				return StorageService.get(this.tokenName)!;
			} else {
				return;
			}
		} catch (error) {
			// There is a technical exception here that occurs when the token is modified or expired.
			return;
		}
	}

	public getUrlString = (urlPart: string = '', base: string = this._httpConfig!.backendUrl): string | undefined => {
		try {
			const url = new URL(urlPart, base);
			return url.pathname;
		} catch {
			return;
		}
	}

	/**
	 * Sets the base request options
	 */
	private setDefaults = (): Promise<this> => new Promise((resolve) => {
		this._axios = axios.create({
			adapter: require('../../../node_modules/axios/lib/adapters/http.js'),
			baseURL: this._httpConfig!.backendUrl,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			onDownloadProgress: this.onDownloadProgress,
			onUploadProgress: this.onUploadProgress,
			timeout: 0
		});

		if (this.isAuthEnabled()) {
			this.setToken(this.getToken());
		}

		this.setHttpResponse();
		this.setCaseDictionaryInterceptors();
		this.setTransformationInterception();
		this.setTokenInterceptor();
		this.setCatchInterception();
		this.setResponseFormattingInterception();

		return resolve(this);
	})

	/**
	 * Callback for upload progress
	 * @param e
	 */
	private onUploadProgress = (name: string) => (event: ProgressEvent) => {
		const percentCompleted = Math.floor(((event.loaded || 1) * 50) / (event.total || 1));

		EventService.publish(JourneyEvent.TRANSFER_PROGRESS, {
			name,
			status: percentCompleted < 50 ? 'uploading' : 'processing',
			percentage: percentCompleted,
			journeyId: StorageService.get(STORAGE_KEYS.journeyId)
		});
	}

	/**
	 * Callback for download progress
	 * @param e
	 */
	private onDownloadProgress = (name: string) => (event: ProgressEvent) => {
		const percentCompleted = Math.floor(((event.loaded || 1) * 50) / (event.total || 1));

		EventService.publish(JourneyEvent.TRANSFER_PROGRESS, {
			name,
			status: 'downloading',
			percentage: 50 + percentCompleted,
		});
	}

	/**
	 * Puts the token into the Authorization header or removes the header completely.
	 * @param token
	 */
	private setToken = (token?: string): void => {
		if (token) {
			this._axios.defaults.headers.common['Authorization'] = token;
			EventService.publish(JourneyEvent.AUTH_CHANGE, { authorized: true });
		} else {
			delete this._axios.defaults.headers.common['Authorization'];
			EventService.publish(JourneyEvent.AUTH_CHANGE, { authorized: false });
		}
	}

	/**
	 * Puts the token in the session storage or some variable
	 * @param token
	 */
	private writeToken = (token: string): void => {
		if (this.tokenName && this.tokenTTL) {
			StorageService.set(this.tokenName, token, this.tokenTTL);
		}
	}

	/**
	 * Removes the token
	 */
	private removeToken = () => {
		if (this.tokenName) {
			StorageService.remove(this.tokenName);
		}
	}

	/**
	 * Sets the interceptor to convert axios response to generic HttpResponse class.
	 */
	private setHttpResponse = () => {
		this._axios.interceptors.response.use((res: AxiosResponse): HttpResponse<any> => {
			return new HttpResponse<any>(res);
		});
	}

	/**
	 * Sets the interceptors for case dictionary.
	 */
	private setCaseDictionaryInterceptors = () => {
		this._axios.interceptors.request.use((req: any) => {
			req.data = this._caseDictionaryService.convert(req.data, 'uglify');
			return req;
		});

		this._axios.interceptors.response.use((res: HttpResponse<any>) => {
			res.data = this._caseDictionaryService.convert(res.data, 'beautify');
			return res;
		});
	}

	private setTransformationInterception = () => {
		this._axios.interceptors.response.use((res: HttpResponse<any>): HttpResponse<any> => {
			let result = res;

			if (res.config.url) {
				if (res.config.url.match(/\/journey\/get/)) {
					// Will be enabled when getResult is enabled.
					// result = TransformationService.convert('journey-retrieval', res);
				} else if (res.config.url.match(this._httpConfig!.endpoints.upload) && res.data.entryData) {
					result = TransformationService.convert('journey-upload', res);
				}
			}

			return result;
		});
	}

	/**
	 * Sets the interceptor for the token check.
	 */
	private setTokenInterceptor = () => {
		this._axios.interceptors.response.use((res: any): Promise<HttpResponse<any>> => {
			if (res.config.url.match(this._httpConfig!.endpoints.login) && res.data.access_token !== undefined) {
				this.setToken(res.data.access_token);
				this.writeToken(res.data.access_token);
			} else if (res.config.url.match(this._httpConfig!.endpoints.logout)) {
				StorageService.clear();
				this.removeToken();
				this.setToken();
			}

			return res;
		});
	}

	/**
	 * Sets the interceptor for exception handling on general issues.
	 */
	private setCatchInterception = () => {
		this._axios.interceptors.response.use((res) => res, (e: any) => {
			if (e.name === 'NetworkError') {
				throw ExceptionManager.process(new ExceptionManager.NetworkException(TranslationKey.EXCEPTION_NETWORK, e.response));
			} else if (e.response && e.response.status === 400) {
				throw ExceptionManager.process(new ExceptionManager.BadRequestException(TranslationKey.EXCEPTION_BAD_REQUEST, e.response));
			} else if (e.response && e.response.status === 401) {
				if (this.getToken()) {
					this.removeToken();
					this.setToken();
				}
				throw ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION, e.response));
			} else if (e.response && e.response.status === 500) {
				throw ExceptionManager.process(new ExceptionManager.ServerException(TranslationKey.EXCEPTION_SERVER, e.response));
			} else {
				throw e;
			}
		});
	}

	/**
	 * Sets the interceptor for successful response formatting.
	 */
	private setResponseFormattingInterception = () => {
		this._axios.interceptors.response.use((res: HttpResponse<any>): HttpResponse<any> | any => {
			if (this._httpConfig!.responseType === ResponseType.RAW) {
				return res;
			} else {
				return res.data;
			}
		});
	}

	private isAuthEnabled = (): boolean => {
		return this._httpConfig!.authSettings !== false;
	}

}

export default new HttpService();
